import { useEffect, useState, useRef } from "react";
import './stylesheet/Message.css'

function Message() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const chatEndRef = useRef(null);
  const adminId = 13;

  useEffect(() => {
    setLoading(true);
    fetch("https://www.gurukrupaeducation.com/api/get-users.php")
      .then((res) => res.json())
      .then((data) => {
        setUsers(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      })
      .finally(() => setLoading(false));
  }, []);

  const getStudentId = (user) => user?.user_id ?? user?.id;

  const fetchChat = async (studentId) => {
    if (!studentId) return;
    try {
      const res = await fetch(
        `https://www.gurukrupaeducation.com/api/get-messages.php?sender_id=${adminId}&receiver_id=${studentId}&sender_role=admin&receiver_role=student`
      );
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching chat:", error);
    }
  };

  useEffect(() => {
    const studentId = getStudentId(selectedUser);
    if (!studentId) return;

    fetchChat(studentId);
    const interval = setInterval(() => {
      fetchChat(studentId);
    }, 2000);

    return () => clearInterval(interval);
  }, [selectedUser]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const studentId = getStudentId(selectedUser);
    if (!text.trim() || !studentId) return;

    try {
      const res = await fetch("https://www.gurukrupaeducation.com/api/send-message.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sender_id: adminId,
          receiver_id: studentId,
          sender_role: "admin",
          receiver_role: "student",
          message: text.trim(),
        }),
      });

      const data = await res.json();
      if (data.success) {
        setText("");
        fetchChat(studentId);
      } else {
        console.error(data.error || "Message send failed");
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <h2 style={styles.sidebarTitle}>Messages</h2>
          <span style={styles.userCount}>{users.length}</span>
        </div>

        {/* Search */}
        <div style={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          <span style={styles.searchIcon}>🔍</span>
        </div>

        {/* Users List */}
        <div style={styles.usersList}>
          {loading ? (
            <div style={styles.emptyState}>Loading...</div>
          ) : filteredUsers.length === 0 ? (
            <div style={styles.emptyState}>No users found</div>
          ) : (
            filteredUsers.map((u) => {
              const studentId = getStudentId(u);
              const isSelected = getStudentId(selectedUser) === studentId;

              return (
                <div
                  key={studentId}
                  onClick={() => setSelectedUser(u)}
                  style={{
                    ...styles.userItem,
                    ...(isSelected ? styles.userItemActive : {}),
                  }}
                >
                  <div style={styles.userAvatar}>
                    {u.name?.charAt(0).toUpperCase()}
                  </div>
                  <div style={styles.userInfo}>
                    <p style={styles.userName}>{u.name}</p>
                    <p style={styles.userEmail}>{u.email || "No email"}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div style={styles.chatContainer}>
        {selectedUser ? (
          <>
            {/* Header */}
            <div style={styles.chatHeader}>
              <div style={styles.chatHeaderInfo}>
                <div style={styles.chatAvatar}>{selectedUser.name?.charAt(0).toUpperCase()}</div>
                <div>
                  <h3 style={styles.chatHeaderTitle}>{selectedUser.name}</h3>
                  <p style={styles.chatHeaderSubtitle}>Active now</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div style={styles.messagesArea}>
              {messages.length === 0 ? (
                <div style={styles.emptyChat}>
                  <p style={styles.emptyChatText}>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      ...styles.messageWrapper,
                      justifyContent: m.sender_role === "admin" ? "flex-end" : "flex-start",
                    }}
                  >
                    <div
                      style={{
                        ...styles.messageBubble,
                        ...(m.sender_role === "admin"
                          ? styles.messageBubbleAdmin
                          : styles.messageBubbleUser),
                      }}
                    >
                      {m.message}
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div style={styles.inputArea}>
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your message here..."
                style={styles.messageInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <button onClick={sendMessage} style={styles.sendButton}>
                <span>📤</span>
              </button>
            </div>
          </>
        ) : (
          <div style={styles.noChat}>
            <div style={styles.noChatContent}>
              <div style={styles.noChatIcon}>💬</div>
              <h3 style={styles.noChatTitle}>Select a user to start</h3>
              <p style={styles.noChatText}>Choose a user from the list to view and send messages</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    height: "100vh",
    background: "#f8f9fa",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  },

  // Sidebar
  sidebar: {
    width: 280,
    background: "#fff",
    borderRight: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },

  sidebarHeader: {
    padding: "20px 16px",
    borderBottom: "1px solid #f3f4f6",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  sidebarTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: "#1f2937",
  },

  userCount: {
    background: "#e5e7eb",
    color: "#4b5563",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: 12,
    fontWeight: 500,
  },

  searchContainer: {
    position: "relative",
    padding: "12px 16px",
  },

  searchInput: {
    width: "100%",
    padding: "8px 12px 8px 32px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },

  searchIcon: {
    position: "absolute",
    left: 24,
    top: 22,
    fontSize: 14,
    pointerEvents: "none",
  },

  usersList: {
    flex: 1,
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "#d1d5db #f3f4f6",
  },

  emptyState: {
    padding: "32px 16px",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 14,
  },

  userItem: {
    padding: "12px 12px",
    margin: "4px 8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 12,
    borderRadius: "10px",
    transition: "all 0.2s ease",
    borderLeft: "3px solid transparent",
  },

  userItemActive: {
    background: "#f3f4f6",
    borderLeftColor: "#3b82f6",
  },

  userAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 16,
    flexShrink: 0,
  },

  userInfo: {
    flex: 1,
    minWidth: 0,
  },

  userName: {
    margin: "0 0 4px 0",
    fontSize: 14,
    fontWeight: 500,
    color: "#1f2937",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  userEmail: {
    margin: 0,
    fontSize: 12,
    color: "#9ca3af",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // Chat Area
  chatContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    background: "#fff",
  },

  chatHeader: {
    padding: "16px 24px",
    borderBottom: "1px solid #e5e7eb",
    background: "#fff",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },

  chatHeaderInfo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  chatAvatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 18,
  },

  chatHeaderTitle: {
    margin: "0 0 4px 0",
    fontSize: 16,
    fontWeight: 600,
    color: "#1f2937",
  },

  chatHeaderSubtitle: {
    margin: 0,
    fontSize: 12,
    color: "#10b981",
    fontWeight: 500,
  },

  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "20px 24px",
    background: "#f9fafb",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  emptyChat: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  emptyChatText: {
    color: "#9ca3af",
    fontSize: 14,
    margin: 0,
  },

  messageWrapper: {
    display: "flex",
    marginBottom: 4,
  },

  messageBubble: {
    maxWidth: "55%",
    padding: "10px 14px",
    borderRadius: "16px",
    wordWrap: "break-word",
    fontSize: 14,
    lineHeight: 1.4,
    animation: "slideIn 0.3s ease-out",
  },

  messageBubbleAdmin: {
    background: "#3b82f6",
    color: "#fff",
    borderBottomRightRadius: "4px",
  },

  messageBubbleUser: {
    background: "#e5e7eb",
    color: "#1f2937",
    borderBottomLeftRadius: "4px",
  },

  inputArea: {
    padding: "16px 24px",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    gap: 12,
    background: "#fff",
  },

  messageInput: {
    flex: 1,
    padding: "10px 14px",
    border: "1px solid #d1d5db",
    borderRadius: "20px",
    fontSize: 14,
    outline: "none",
    transition: "all 0.2s",
    fontFamily: "inherit",
  },

  sendButton: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "none",
    background: "#3b82f6",
    color: "#fff",
    cursor: "pointer",
    fontSize: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    hover: {
      background: "#2563eb",
      transform: "scale(1.05)",
    },
  },

  noChat: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f9fafb",
  },

  noChatContent: {
    textAlign: "center",
  },

  noChatIcon: {
    fontSize: 64,
    marginBottom: 16,
  },

  noChatTitle: {
    margin: "0 0 8px 0",
    fontSize: 18,
    fontWeight: 600,
    color: "#1f2937",
  },

  noChatText: {
    margin: 0,
    fontSize: 14,
    color: "#9ca3af",
  },
};

export default Message;